import styled, { css, keyframes } from 'styled-components';
import { transparentize } from 'polished';
import { darkModeLiquidGlass } from 'src/utils/darkModeLiquidGlass';

export const Wrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: ${theme.sz.s4};
    background-color: ${theme.co.neutral99};
    border: ${theme.bo.thinWidth} solid ${theme.co.neutral95};
    border-radius: ${theme.bo.mediumLarge};
    box-shadow: ${theme.sh.s1};
    overflow: hidden;
    ${darkModeLiquidGlass};

    /* Remove chrome when used in SpringCleaningOverview */
    .spring-cleaning-overview & {
      margin-bottom: 0;
      background: none;
      border: none;
      box-shadow: none;
      border-radius: 0;
      overflow: visible;
    }

    html[data-color-scheme='dark'] .spring-cleaning-overview & {
      background: none;
      backdrop-filter: none;
      box-shadow: none;
    }
  `,
);

export const CardHeader = styled.header(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s3};
    padding: ${theme.sz.s3} ${theme.sz.s5};
    background-color: ${theme.co.primary98};
    border-bottom: ${theme.bo.thinWidth} solid ${theme.co.primary90};

    html[data-color-scheme='dark'] & {
      background-color: ${transparentize(0.85, theme.co.primary98)};
      border-bottom-color: ${transparentize(0.82, theme.co.primary90)};
    }

    @media (max-width: ${theme.sz.lg}) {
      flex-direction: column;
      align-items: stretch;
      gap: ${theme.sz.s2};
      padding: ${theme.sz.s3} ${theme.sz.s3};
    }

    .spring-cleaning-overview & {
      padding: 0 0 ${theme.sz.s3};
      background: none;
      border-bottom: none;
    }

    html[data-color-scheme='dark'] .spring-cleaning-overview & {
      background: none;
      border-bottom: none;
    }
  `,
);

export const CurrentWordlistLink = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s2};
    min-width: 0;
    flex: 1;
  `,
);

export const CurrentWordlistImage = styled.img(
  ({ theme }) => css`
    display: block;
    width: ${theme.sz.s11};
    height: ${theme.sz.s11};
    border-radius: ${theme.bo.mediumSmall};
    object-fit: cover;
    flex-shrink: 0;
    box-shadow: 0 0 0 ${theme.bo.mediumWidth} ${theme.co.primary20};

    @media (max-width: ${theme.sz.lg}) {
      width: ${theme.sz.s10};
      height: ${theme.sz.s10};
    }
  `,
);

export const CurrentWordlistLabel = styled.span(
  ({ theme }) => css`
    font-size: ${theme.sz.s3};
    font-weight: ${theme.DEPRECATED_ty.bold};
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${theme.co.secondary50};
  `,
);

export const CurrentWordlistTitle = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    color: ${theme.co.secondary20};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
      white-space: normal;
    }
  `,
);

export const PracticeVocabTitle = styled.h2(
  ({ theme }) => css`
    margin: 0;
    ${theme.typography.heading.desktop.s};
    color: ${theme.co.secondary20};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.s};
    }
  `,
);

export const CurrentWordlistSubline = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    color: ${theme.co.secondary50};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: ${theme.sz.lg}) {
      white-space: normal;
    }
  `,
);

export const CurrentWordlistSublineTitle = styled.strong(
  ({ theme }) => css`
    font-weight: ${theme.DEPRECATED_ty.bold};
    color: ${theme.co.secondary20};
  `,
);

export const CurrentWordlistInfo = styled.div(
  ({ theme }) => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${theme.sz.s1};
    min-width: 0;
  `,
);

export const CurrentWordlistTitleRow = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.sz.s2};
    min-width: 0;
  `,
);

export const ChangeWordlistButtonWrap = styled.div(
  ({ theme }) => css`
    flex: 0 1 auto;
    max-width: 100%;

    & a {
      max-width: 100%;
      border-radius: ${theme.bo.mediumSmall};

      svg {
        width: ${theme.sz.s4};
        height: ${theme.sz.s4};
      }
    }
  `,
);

export const ListProgress = styled.div(
  ({ theme }) => css`
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${theme.sz.s1};
    flex: none;

    @media (max-width: ${theme.sz.lg}) {
      margin-left: 0;
      width: 100%;
      align-items: stretch;
      padding-left: calc(${theme.sz.s10} + ${theme.sz.s2});
    }
  `,
);

export const ListProgressLabel = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    font-weight: ${theme.DEPRECATED_ty.semiBold};
    color: ${theme.co.secondary50};
  `,
);

export const ListProgressTrack = styled.div(
  ({ theme }) => css`
    width: ${theme.sz.s44};
    height: ${theme.sz.s2};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.secondary95};
    border: ${theme.bo.thinWidth} solid ${theme.co.secondary80};
    overflow: hidden;

    @media (max-width: ${theme.sz.lg}) {
      width: 100%;
    }
  `,
);

export const ListProgressFill = styled.div<{ $percentComplete: number }>(
  ({ theme, $percentComplete }) => css`
    height: 100%;
    width: ${Math.max(0, Math.min($percentComplete, 100))}%;
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary20};
  `,
);

export const MainColumn = styled.div<{ $usePreviewGrid?: boolean; $showMediaColumn?: boolean }>(
  ({ theme, $usePreviewGrid, $showMediaColumn }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
    min-width: 0;
    padding: ${theme.sz.s5};

    .spring-cleaning-overview & {
      padding: 0;
    }

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s3};
      gap: ${theme.sz.s3};
    }

    ${
      $usePreviewGrid &&
      css`
        display: grid;
        grid-template-columns: minmax(0, 1fr) ${$showMediaColumn ? 'auto' : '0fr'};
        grid-template-rows: auto auto;
        align-items: start;
        column-gap: ${$showMediaColumn ? theme.sz.s6 : '0'};
        transition:
          grid-template-columns 450ms ease-out,
          column-gap 450ms ease-out;

        @media (max-width: ${theme.sz.lg}) {
          display: flex;
          flex-direction: column;
        }
      `
    }
  `,
);

export const MainColumnTop = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
    min-width: 0;

    ${MainColumn}[data-preview-grid='true'] & {
      grid-column: 1;
      grid-row: 1;
    }

    @media (max-width: ${theme.sz.lg}) {
      ${MainColumn}[data-preview-grid='true'] & {
        order: 3;
      }
    }
  `,
);

export const StartLessonButtonWrap = styled.div(
  ({ theme }) => css`
    margin-top: ${theme.sz.s1};

    ${MainColumn}[data-preview-grid='true'] & {
      grid-column: 1;
      grid-row: 2;
    }

    @media (max-width: ${theme.sz.lg}) {
      ${MainColumn}[data-preview-grid='true'] & {
        order: 2;
        margin-top: 0;
        width: 100%;
      }
    }
  `,
);

export const PreviewVideoAside = styled.div(
  ({ theme }) => css`
    overflow: hidden;
    min-width: 0;
    min-height: 0;

    ${MainColumn}[data-preview-grid='true'] & {
      grid-column: 2;
      grid-row: 1 / -1;
      align-self: stretch;
      display: flex;
      align-items: stretch;
      justify-content: flex-end;

      > * {
        height: 100%;
      }
    }

    @media (max-width: ${theme.sz.lg}) {
      ${MainColumn}[data-preview-grid='true'] & {
        display: block;
        width: 100%;
        order: 1;
        align-self: center;

        > * {
          height: auto;
        }
      }
    }
  `,
);

export const PreviewSectionExpand = styled.div<{ $isOpen: boolean }>(
  ({ $isOpen }) => css`
    display: grid;
    grid-template-rows: ${$isOpen ? '1fr' : '0fr'};
    transition: grid-template-rows 400ms ease-out;
  `,
);

export const PreviewSectionInner = styled.div(
  () => css`
    overflow: hidden;
    min-height: 0;
  `,
);

const previewContentFadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

export const PreviewContentFadeIn = styled.div(
  () => css`
    animation: ${previewContentFadeIn} 280ms ease-out;
  `,
);
